'use client'

import {createContext, useContext, ReactNode, useEffect, useRef} from 'react'
import {useFullScreenAd} from '@gamezop/ads-sdk/use-fullscreen-ad'
import {FullScreenAdEvents} from '@gamezop/ads-sdk/types'
import {handleFullScreenAds} from '@app/components/game-player/analytics/fullscreen-ads'
import {broadcastMessage, decodeUriAndSanatizeName} from '../../helper'
import {checkIfAdIsFrequencyCapped, throttleAdFrequency} from '../../utils/throttle-ad'
import {focusGameIframe, muteGame, pauseAndMute, playAndUnmute, unmuteGame} from '../../game-functions'
import {usePlayButton} from '@app/components/gameplayer-page/game-image-and-play-btn/play-btn/play-button-context'

export const FREQUENCY_CAP = 45
const FREQUENCY_CAP_IN_MS = FREQUENCY_CAP * 1000 // 45 seconds
const PRELOAD_TIME = FREQUENCY_CAP_IN_MS - 5000

type GamePlayerAdContextType = {
  showPrerollAd: () => void
  showMidrollAd: () => void
  showRewardedAd: () => void
  isAdEnabled: boolean
}

const GamePlayerAdContext = createContext<GamePlayerAdContextType | null>(null)

export const useGamePlayerAds = () => {
  const context = useContext(GamePlayerAdContext)
  if (!context) {
    throw new Error('useGamePlayerAds must be used within a GamePlayerAdProvider')
  }
  return context
}

interface GamePlayerAdProviderProps {
  children: ReactNode
  isAdEnabled: boolean
  gameNameEn: string
  gameCode: string
}

export const GamePlayerAdProviderClient = ({
  children,
  isAdEnabled,
  gameNameEn,
  gameCode,
}: GamePlayerAdProviderProps) => {
  const preloadTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const {gameScreenShown} = usePlayButton()

  const {triggerAd: preroll} = useFullScreenAd({
    adSpot: 'gamezopGameplayerPreroll',
    customParams: {
      gameCode,
      game: decodeUriAndSanatizeName(gameNameEn),
    },
    config: {timeout: 4000},
  })
  const {triggerAd: midroll, preloadAd} = useFullScreenAd({
    adSpot: 'gamezopGameplayerMidroll',
    customParams: {
      gameCode,
      game: decodeUriAndSanatizeName(gameNameEn),
    },
  })
  const {triggerAd: rewarded} = useFullScreenAd({
    adSpot: 'gamezopGameplayerRewarded',
    customParams: {
      gameCode: gameCode,
      game: decodeUriAndSanatizeName(gameNameEn),
    },
  })

  useEffect(() => {
    return () => {
      if (preloadTimeoutRef.current) {
        clearTimeout(preloadTimeoutRef.current)
      }
    }
  }, [])

  function preLoadMidrollAd() {
    // Clear any existing timeout before setting a new one
    if (preloadTimeoutRef.current) {
      clearTimeout(preloadTimeoutRef.current)
    }

    preloadTimeoutRef.current = setTimeout(() => {
      preloadAd((event, adSpot, adDemand) => {
        handleFullScreenAds(event, adSpot, adDemand, true, gameNameEn, gameCode)
      })
    }, PRELOAD_TIME)
  }

  function showPrerollAd() {
    if (!isAdEnabled || checkIfAdIsFrequencyCapped()) return

    preroll((event, adSpot, adDemand) => {
      handleFullScreenAds(event, adSpot, adDemand, true, gameNameEn, gameCode)
      if (event === FullScreenAdEvents.AD_CLOSED) {
        focusGameIframe()
        // this throttleAdFrequency handles frequency timer
        throttleAdFrequency()
        if (gameScreenShown) {
          unmuteGame()
        }
      }
    })
    // this throttleAdFrequency handles de-duplication
    throttleAdFrequency()
    preLoadMidrollAd()

    muteGame()
  }

  function showMidrollAd() {
    if (!isAdEnabled) return

    if (checkIfAdIsFrequencyCapped()) {
      broadcastMessage(gameNameEn, 'ad-unavailable')
      return
    }

    midroll((event, adSpot, adDemand) => {
      handleFullScreenAds(event, adSpot, adDemand, true, gameNameEn, gameCode)
      if (event === FullScreenAdEvents.AD_CLOSED) {
        focusGameIframe()
        // this throttleAdFrequency handles frequency timer
        throttleAdFrequency()
        broadcastMessage(gameNameEn, event === FullScreenAdEvents.AD_CLOSED ? 'ad-complete' : 'ad-unavailable')
        playAndUnmute()
      }
    })
    // this throttleAdFrequency handles de-duplication
    throttleAdFrequency()
    preLoadMidrollAd()
    pauseAndMute()
  }

  function showRewardedAd() {
    if (!isAdEnabled) {
      broadcastMessage(gameNameEn, 'ad-unavailable')
      return
    }
    rewarded((event, adSpot, adDemand) => {
      handleFullScreenAds(event, adSpot, adDemand, true, gameNameEn, gameCode)
      if (event === FullScreenAdEvents.AD_CLOSED) {
        focusGameIframe()
        playAndUnmute()
      }
      if (event === FullScreenAdEvents.AD_VIEWED) {
        broadcastMessage(gameNameEn, 'ad-complete')
      }
      if (event === FullScreenAdEvents.AD_DISMISSED) {
        broadcastMessage(gameNameEn, 'ad-skipped')
      }
      if (event === FullScreenAdEvents.ALL_ADS_FAILED) {
        broadcastMessage(gameNameEn, 'ad-unavailable')
      }
    })
    pauseAndMute()
  }

  return (
    <GamePlayerAdContext.Provider
      value={{
        showPrerollAd,
        showMidrollAd,
        showRewardedAd,
        isAdEnabled,
      }}
    >
      {children}
    </GamePlayerAdContext.Provider>
  )
}
